// Camp and vendor master records (admin/src/routes/master-records.ts → commerce routes/master-records.ts).
// Writes are partial: a body names only the fields it changes, and the answer is the whole record again.
import { j, type OpsChange } from './api';

export type Json = string | number | boolean | null | Array<string | number>;
export type Body = Record<string, Json>;

export interface Cohort { id: number; code: string; camps: number }
export interface RecordOptions {
  cohorts: Cohort[]; campTypes: string[]; foodTypes: string[]; locations: string[]; jobTitles: string[];
  limitPeriods: string[]; packageEmail: string[]; minOrderPer: string[];
}
export interface Category { id: number; level: number; name: string }

export interface CampRow {
  id: number; name: string; status: number; is_demo: boolean; cohort_id: number | null; cohort: string | null;
  camp_type: string | null; physical_location: string | null; account_id: string | null; vendors: number; users: number;
}
export interface Camp {
  id: number; name: string; legal_name: string | null; email: string | null; telephone: string | null; status: number;
  is_demo: boolean; cohort_id: number | null; cohort: string | null; super_user_id: number | null; city: string | null;
  region: string | null; postcode: string | null; onboard_status: string | null;
}
export interface CampProfile {
  account_id: string | null; ap_email: string | null; bill_pay_email: string | null; sum_dropbox_url: string | null;
  dropbox_path: string | null; physical_location: string | null; camp_type: string | null; food_type: string | null;
  year_round: boolean | null; primary_contact: string | null; primary_contact_role: string | null; notes: string | null;
  tax_exempt: boolean | null; can_create_sublogins: boolean | null; max_sublogins: number | null; invoice_review: boolean | null;
  auto_lock_packages: boolean | null; show_checkout_alert: boolean | null; follow_group_products: boolean | null;
  gl_codes: string[]; blocked_category_ids: number[];
}
export interface CampUser {
  id: number; name: string | null; email: string | null; is_active: boolean | null; master: boolean; title: string | null;
  is_approver: boolean | null; order_limit_period: string | null; order_limit_value: number | null; approver_ids: number[];
  package_email: string | null; blocked_category_ids: number[];
  place_order: boolean | null; order_approval_needed: boolean | null; approve_orders: boolean | null;
  /** the last permission template applied to them ('<id> <name>'), or null */
  template: string | null;
}
export interface PairDelivery {
  is_active: boolean; account_number: string | null; min_order_amount: number | null; min_order_qty: number | null;
  allowed_week_days: string | null; cutoff_time: string | null; order_lead_time: string | null;
}
export interface CampVendor extends PairDelivery { vendor_id: number; vendor: string | null }
export interface CampRecord { camp: Camp; profile: CampProfile | null; users: CampUser[]; vendors: CampVendor[]; changes: OpsChange[] }

export interface VendorRecordVendor {
  id: number; name: string | null; email: string | null; account_id: string | null; is_edi: boolean | null; is_active: boolean | null;
  delivery_date_required: boolean | null; auto_approve_feed: boolean | null; report_vendor_emails: string | null;
}
export interface VendorProfile {
  edi_opcode: string | null; dropbox_path: string | null; dropbox_file_name: string | null; primary_contact: string | null;
  office_phone: string | null; cell_phone: string | null; custsvc_phone: string | null; vendor_note: string | null;
  notes: string | null; internal_inv_notes: string | null; display_on_po: string | null; min_order_amount: number | null;
  min_order_per: string | null; min_order_qty: number | null; cutoff_time: string | null; order_lead_time: string | null;
  allowed_week_days: number[] | null; excluded_days: string[]; sibling_of: number | null; is_parent: boolean | null;
  related_vendor_ids: number[]; visible_on_vendor_list: boolean | null; show_checkout_alert: boolean | null;
  invoice_review: boolean | null; auto_lock_packages: boolean | null;
}
export interface FeedMap { item_col: number | null; case_col: number | null; split_col: number | null; splits_included: boolean | null }
export interface NamedId { id: number; name: string | null }
export interface VendorCampDelivery extends PairDelivery { org_id: number; camp: string }
export interface VendorRecord {
  vendor: VendorRecordVendor; profile: VendorProfile | null; feedMap: FeedMap | null; related: NamedId[]; siblings: NamedId[];
  bids: Array<{ id: number; name: string; sku_prefix: string | null }>; camps: VendorCampDelivery[]; changes: OpsChange[];
}

// Buyer permission templates (Adobe's Customers › Manage Template) — commerce server/schema/032.
/** One resource of Adobe's company-role tree, and the Railway column applying a template writes it to (null: none). */
export interface AclResource { id: string; label: string; parent: string | null; railway: string | null }
export interface PermissionTemplate {
  id: number; name: string; is_active: boolean; allowed_resources: string[]; blocked_category_ids: number[];
  order_limit_period: string | null; order_limit_value: number | string | null; package_email: string | null;
  magento_template_id: number | null; created_at: string; created_by: string | null; updated_at: string; updated_by: string | null;
}
export interface TemplateList { templates: PermissionTemplate[]; resources: AclResource[]; settings: Record<string, string> }
export interface ApplyResult extends CampRecord { template: number; applied: Array<{ userId: number; changed: string[] }> }

const put = <T>(path: string, body: Body) => j<T>(path, { method: 'PUT', body: JSON.stringify(body) });
type Changed = { changed: string[] };

export const records = {
  options: () => j<RecordOptions>('/commerce/master-records/options'),
  categories: () => j<Category[]>('/commerce/categories'),
  camps: (q: string, status: string) => j<CampRow[]>(`/commerce/camps?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`),
  camp: (id: number) => j<CampRecord>(`/commerce/camps/${id}`),
  saveCamp: (id: number, body: Body) => put<CampRecord & Changed>(`/commerce/camps/${id}`, body),
  saveGlCodes: (id: number, glCodes: string[]) => put<CampRecord & Changed>(`/commerce/camps/${id}/gl-codes`, { glCodes }),
  saveCampBlocks: (id: number, categoryIds: number[]) => put<CampRecord & Changed>(`/commerce/camps/${id}/category-blocks`, { categoryIds }),
  saveCampUser: (id: number, userId: number, body: Body) => put<CampRecord & Changed>(`/commerce/camps/${id}/users/${userId}`, body),
  assignVendor: (id: number, vendorId: number) =>
    j<CampRecord & { created: boolean }>(`/commerce/camps/${id}/vendors`, { method: 'POST', body: JSON.stringify({ vendorId }) }),
  saveCampVendor: (id: number, vendorId: number, body: Body) => put<Changed>(`/commerce/camps/${id}/vendors/${vendorId}`, body),
  applyTemplate: (id: number, templateId: number, userIds: number[]) =>
    j<ApplyResult>(`/commerce/camps/${id}/apply-template`, { method: 'POST', body: JSON.stringify({ templateId, userIds }) }),
  templates: () => j<TemplateList>('/commerce/permission-templates'),
  template: (id: number) => j<{ template: PermissionTemplate; changes: OpsChange[] }>(`/commerce/permission-templates/${id}`),
  createTemplate: (body: Body) => j<{ template: PermissionTemplate }>('/commerce/permission-templates', { method: 'POST', body: JSON.stringify(body) }),
  saveTemplate: (id: number, body: Body) => put<{ changed: string[]; template: PermissionTemplate; changes: OpsChange[] }>(`/commerce/permission-templates/${id}`, body),
  vendor: (id: number) => j<VendorRecord>(`/commerce/vendors/${id}/record`),
  saveVendor: (id: number, body: Body) => put<VendorRecord & Changed>(`/commerce/vendors/${id}/record`, body),
  saveVendorCamp: (id: number, orgId: number, body: Body) => put<Changed>(`/commerce/vendors/${id}/camps/${orgId}/delivery`, body),
};
